<template>
  <div class="averages">
    <v-row>
      <div class="label">{{ $t(`map.popover.average`) }}</div>
    </v-row>
    <v-row>
      <PopoverAverage
        data-cy="pocHalfHour"
        :value="averages.halfHour"
        name="halfHour"
        :aqiType="aqiType"
        :measurementType="measurementType"
        class="hide-xxs"
      />
      <PopoverAverage
        data-cy="pocOneHour"
        :value="averages.oneHour"
        name="oneHour"
        :aqiType="aqiType"
        :measurementType="measurementType"
      />
      <PopoverAverage
        data-cy="pocSixHour"
        :value="averages.sixHour"
        name="sixHour"
        :aqiType="aqiType"
        :measurementType="measurementType"
        class="hide-xs"
      />
      <PopoverAverage
        data-cy="pocTwentyFourHour"
        :value="averages.oneDay"
        name="oneDay"
        :aqiType="aqiType"
        :measurementType="measurementType"
      />
      <PopoverAverage
        data-cy="pocOneWeek"
        :value="averages.oneWeek"
        name="oneWeek"
        :aqiType="aqiType"
        :measurementType="measurementType"
      />
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PopoverAverage from './aqiAverage.vue'
import { useAqiValidator } from '@/services/map'
import { measurementTypes, aqiTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import type { AveragePM } from '@/types/devices'
import type { GetterByDeviceId } from '@/components/devicePopover/types.ts'

type AverageValue = number | string | null
interface AqiAveragesData {
  halfHour: AverageValue
  oneHour: AverageValue
  sixHour: AverageValue
  twelveHour: AverageValue
  oneDay: AverageValue
  oneWeek: AverageValue
}

export default defineComponent({
  name: 'aqiAverages',
  props: {
    deviceId: { type: String, default: '' },
    aqiType: { type: Number, default: 0 },
    measurementType: { type: Number, default: 0 }
  },
  components: {
    PopoverAverage
  },
  setup() {
    const { validateAQI } = useAqiValidator()
    return { validateAQI }
  },
  computed: {
    id(): string {
      return this.deviceId || ''
    },
    averages(): AqiAveragesData {
      let averages: AqiAveragesData = {
        halfHour: null,
        oneHour: null,
        sixHour: null,
        twelveHour: null,
        oneDay: null,
        oneWeek: null
      }
      const measType = this.$store.getters[
        `${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`
      ] as number
      const isIndia = this.aqiType === aqiTypes.in
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_AVERAGES_BY_DEVICE_ID}`
      ] as GetterByDeviceId<AveragePM>
      const avgs = getter(this.id)
      if (avgs) {
        if (measType === measurementTypes.pm25) {
          averages = isIndia ? this.getAverages('2x5_india', avgs) : this.getAverages('2x5', avgs)
        }
        if (measType === measurementTypes.pm10) {
          averages = isIndia ? this.getAverages('10_india', avgs) : this.getAverages('10', avgs)
        }
      }
      return averages
    }
  },
  methods: {
    getAverages(aqiType: string, avgs: AveragePM): AqiAveragesData {
      const toAqi = (key: string): AverageValue => {
        const value = avgs[key as keyof AveragePM] as number
        return this.validateAQI(value ?? Number.NaN)
      }

      return {
        halfHour: toAqi(`pm${aqiType}_aqi_30m`),
        oneHour: toAqi(`pm${aqiType}_aqi_1h`),
        sixHour: toAqi(`pm${aqiType}_aqi_6h`),
        twelveHour: toAqi(`pm${aqiType}_aqi_12h`),
        oneDay: toAqi(`pm${aqiType}_aqi_1d`),
        oneWeek: toAqi(`pm${aqiType}_aqi_1w`)
      }
    },
    loadData(): void {
      if (this.deviceId) {
        this.$store.dispatch(
          `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_TELEMETRY_AVERAGES_AND_UPDATE}`,
          this.deviceId
        )
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.averages {
  padding: 12px 16px 0 0;

  .label {
    margin-bottom: 0.2rem;
    text-transform: uppercase;
    font-size: 13px;
    font-weight: 300;
  }

  .widget-xxs &,
  .widget-xs &,
  .widget-sm & {
    padding: 12px 12px 0 0;
    .row {
      flex-wrap: nowrap;
    }
  }

  .widget-xs &,
  .widget-xxs & {
    .hide-xs {
      display: none;
    }
  }
  .widget-xxs & {
    .hide-xxs {
      display: none;
    }
  }
}
</style>
